import React from "react";
import { DidYouKnow, TrustpilotQuote } from "./QuizSupport";
import { quizAsset } from "./quizAssets";
import type { QuizCutout } from "./QuizShell";

/**
 * Per-question screen decor from the Figma quiz redesign: which support block
 * ("Did you know?" fact or Trustpilot quote) and which bottom-anchored dog
 * cut-out photo each question screen gets. Keyed by sub-step id.
 */

const FACTS: Record<string, string> = {
  breed:
    "The UK is home to over 200 recognised dog breeds — and every single one of them can thrive on fresh, gently cooked food.",
  sex: "Male dogs typically need slightly more calories than females of the same size — our recipes are portioned with that in mind.",
  reproduction:
    "Neutered dogs burn around 20–30% fewer calories, so their portions need adjusting to keep them at a healthy weight.",
  weight:
    "Over half of UK dogs are overweight — portion-controlled fresh food is one of the easiest ways to keep them lean and full of energy.",
};

const QUOTES: Record<string, { title: string; quote: string; author: string }> = {
  age: {
    title: "My dogs love Paws on Plates",
    quote:
      "I started feeding Paws on Plates to my two elderly Portuguese Water Dogs a couple of months ago. They both love it, plus this food has made a very noticeable difference, especially in my ten year old, better stools, her coat shines, she has more energy, fetching toys and playing like a youngster.  My 14 year old has tummy issues, she has Paws on Plates as a topper on her veterinary gastrointestinal dry food, this is working really well for her.  Five stars for Paws on Plates, they have a great customer service too.",
    author: "Wendy",
  },
  body: {
    title: "Helping with weight control",
    quote:
      "Just moved my dog onto Paws on Plates and it's been a great change. He's always struggled a bit with weight, but having proper portioned meals with real ingredients has made it much easier to manage. He's looking leaner already and full of energy. It's nice to have something that seems to be working for him and enjoys!",
    author: "S Young",
  },
  activity: {
    title: "100% recommend",
    quote:
      "I love this company. Whenever I've had concerns, they've responded quickly, and the dog food is excellent. You can clearly see the chunks of vegetables, which I think are so important for our dogs. My dog is loving it and is full of energy. I also love their eco-friendly packaging.",
    author: "Roxanne",
  },
  treats: {
    title: "A family business with eco-friendly packaging and exceptional food",
    quote:
      "My boys' favourite flavours are: Lamb, Beef and Turkey, and Nanna's Nibbles (treats we play hunt the biscuit with!) and the Pizzle. The Pizzle is the first hard chew in ages that my older Chihuahua has truly enjoyed. It keeps the dogs occupied for ages.",
    author: "Maria",
  },
  allergies: {
    title: "This food is fantastic",
    quote:
      "This food is fantastic, my dog absolutely loves it. He had itchy ears and since transitioning to this food, the itching has significantly stopped. If you have the freezer space, I would recommend fresh from this brand.",
    author: "Archie",
  },
};

export function supportForQuestion(questionId: string | null | undefined): React.ReactNode {
  if (!questionId) return null;
  const fact = FACTS[questionId];
  if (fact) return <DidYouKnow text={fact} />;
  const quote = QUOTES[questionId];
  if (quote) return <TrustpilotQuote {...quote} />;
  return null;
}

export function cutoutForQuestion(questionId: string | null | undefined): QuizCutout | undefined {
  switch (questionId) {
    case "reproduction":
      return {
        src: quizAsset("cutout-dachshund"),
        side: "left",
        width: { xs: 330, md: 540 },
        clearance: { xs: 210, md: 0 },
      };
    case "activity":
      return {
        src: quizAsset("cutout-frenchie"),
        side: "right",
        width: { xs: 175, md: 330 },
        clearance: { xs: 150, md: 0 },
      };
    case "owner":
      return {
        src: quizAsset("cutout-collie-bowl"),
        side: "right",
        width: { xs: 270, md: 400 },
        clearance: { xs: 300, md: 0 },
      };
    default:
      return undefined;
  }
}
